import { clientStore } from "./clientStorage";
import {
  valueFitsPolicy,
  WALKTHROUGH_SESSION_KEY,
  WALKTHROUGH_SESSION_POLICY,
} from "./persistenceRegistry";

const failureKey = `session:${WALKTHROUGH_SESSION_KEY}`;
let memoryCompleted = false;

const markVerified = () => {
  clientStore.clearDurabilityFailures((key) => key === failureKey);
};

export interface WalkthroughSessionRead {
  completed: boolean;
  verified: boolean;
}

/** Guard the session-only walkthrough marker so disabled storage cannot crash App render. */
export const walkthroughSessionStore = {
  read(): WalkthroughSessionRead {
    try {
      const raw = window.sessionStorage.getItem(WALKTHROUGH_SESSION_KEY);
      if (raw === null) {
        markVerified();
        return { completed: memoryCompleted, verified: true };
      }
      if (
        !valueFitsPolicy(raw, WALKTHROUGH_SESSION_POLICY) ||
        !WALKTHROUGH_SESSION_POLICY.validate?.(raw)
      ) {
        clientStore.reportMemoryOnly("invalid", failureKey);
        return { completed: memoryCompleted, verified: false };
      }
      memoryCompleted = raw === "true";
      markVerified();
      return { completed: memoryCompleted, verified: true };
    } catch {
      clientStore.reportMemoryOnly("unavailable", failureKey);
      return { completed: memoryCompleted, verified: false };
    }
  },

  setCompleted(completed = true): boolean {
    memoryCompleted = completed;
    try {
      if (completed) {
        window.sessionStorage.setItem(WALKTHROUGH_SESSION_KEY, "true");
      } else {
        window.sessionStorage.removeItem(WALKTHROUGH_SESSION_KEY);
      }
      const durable = window.sessionStorage.getItem(WALKTHROUGH_SESSION_KEY);
      if ((completed && durable !== "true") || (!completed && durable !== null)) {
        clientStore.reportMemoryOnly("unavailable", failureKey);
        return false;
      }
      markVerified();
      return true;
    } catch {
      clientStore.reportMemoryOnly("unavailable", failureKey);
      return false;
    }
  },

  clear(): boolean {
    return this.setCompleted(false);
  },

  resetForTests(): void {
    memoryCompleted = false;
  },
};
